// Pictures for the screens. A project points at bytes by content hash; the
// store's media cache holds the bytes; a bundled drawing is a file under
// /art. These helpers turn either into something an <img> can show.
import { useEffect, useState } from 'react';
import type { Project } from '@shared/plan';
import { store } from './store';

// The signed-in person's list, so a picture this device has never seen (a
// blueprint a run just drew, a photo taken on another device) can be fetched
// once and kept. Set by the session; null for a visitor, who has only the
// pictures on the device.
let remoteList: string | null = null;
export function setRemoteMediaList(listId: string | null): void { remoteList = listId; }

async function fetchRemote(hash: string): Promise<Blob | undefined> {
  if (!remoteList || typeof fetch !== 'function') return undefined;
  const res = await fetch(`/api/media/${encodeURIComponent(remoteList)}/${encodeURIComponent(hash)}`, { credentials: 'same-origin' });
  if (!res.ok) return undefined;
  const blob = await res.blob();
  await store.media.put(hash, blob);
  return blob;
}

/** An object URL for a hash: from the device, else from the list on the server. Undefined while loading or missing. Revoked on unmount. */
export function useMediaUrl(hash: string | undefined): string | undefined {
  // Keyed by hash, so a stale URL is never shown for a new hash while it loads.
  const [loaded, setLoaded] = useState<{ hash: string; url: string }>();
  useEffect(() => {
    if (!hash) return;
    let current: string | undefined;
    let cancelled = false;
    void store.ready.then(() => store.media.get(hash)).then((blob) => blob ?? fetchRemote(hash)).then((blob) => {
      if (cancelled || !blob) return;
      current = URL.createObjectURL(blob);
      setLoaded({ hash, url: current });
    }).catch(() => {
      // The store closed under it (a sign-out wipe): no picture, and the fresh store's screens ask again.
    });
    return () => { cancelled = true; if (current) URL.revokeObjectURL(current); };
  }, [hash]);
  return hash && loaded?.hash === hash ? loaded.url : undefined;
}

/** The bundled blueprint drawing a project falls back to before it has one of its own. */
export const artworkSrc = (p: Project): string => `/art/${p.artwork ?? 'blueprint-generic'}.svg`;

/** The project's blueprint: its own by hash, else the bundled drawing. */
export function useBlueprintSrc(p: Project): string {
  const own = useMediaUrl(p.blueprintHash);
  return own ?? artworkSrc(p);
}

/** The project's photo, or undefined when it has none (demo and template projects). */
export const usePhotoSrc = (p: Project): string | undefined => useMediaUrl(p.photoHash);
